import React, { useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Bus,
  Heart,
  Utensils,
  PartyPopper,
  Home,
  Plus,
  MapPin,
  Users as UsersIcon,
  CalendarCheck,
  Check,
  X,
  Clock,
  Pencil,
} from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { levelDisplayName, levelWord, levelSystemOf } from "@/lib/levelLabels";
import {
  AnnouncementAttachmentList,
  AnnouncementAttachmentPicker,
  attachmentItemsFromRecord,
} from "@/components/AnnouncementAttachments";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const EVENT_COPY = {
  en: {
    allowRsvp: "Allow parent RSVPs",
    allGuardians: "All guardians",
    audience: "Audience",
    chooseGrade: "Choose grade",
    dateRequired: "Enter both start and end dates.",
    description: "Description",
    editEvent: "Edit event",
    endDate: "End date",
      end: "End time",
    eventPublished: "Event published",
    eventUpdated: "Event updated",
    invalidDateRange: "End date must be on or after start date.",
    invalidTime: "End time must be after start time.",
    loadError: "Unable to load events.",
    name: "Name",
    nameRequired: "Enter an event name.",
    newEvent: "New event",
    noEvents: "No events have been published.",
    publish: "Publish",
    publishing: "Publishing...",
    saveChanges: "Save changes",
    retry: "Retry",
    rsvpClosed: "RSVP closed",
    rsvpOpen: "RSVP open",
    grade: "Specific grade",
    startDate: "Start date",
      start: "Start time",
    venue: "Venue",
    yes: "yes",
  },
  ar: {
    allowRsvp: "السماح بتأكيدات أولياء الأمور",
    allGuardians: "جميع أولياء الأمور",
    audience: "الجمهور",
    chooseGrade: "اختر الصف",
    dateRequired: "أدخل تاريخ البداية وتاريخ النهاية.",
    description: "الوصف",
    editEvent: "تعديل الفعالية",
    endDate: "تاريخ النهاية",
      end: "وقت الانتهاء",
    eventPublished: "تم نشر الفعالية",
    eventUpdated: "تم تحديث الفعالية",
    invalidDateRange: "يجب أن يكون تاريخ النهاية في نفس يوم البداية أو بعده.",
    invalidTime: "يجب أن يكون وقت النهاية بعد وقت البدء.",
    loadError: "تعذر تحميل الفعاليات.",
    name: "الاسم",
    nameRequired: "أدخل اسم الفعالية.",
    newEvent: "فعالية جديدة",
    noEvents: "لا توجد فعاليات منشورة.",
    publish: "نشر",
    publishing: "جارٍ النشر...",
    saveChanges: "حفظ التغييرات",
    retry: "إعادة المحاولة",
    rsvpClosed: "التأكيد مغلق",
    rsvpOpen: "التأكيد متاح",
    grade: "صف محدد",
    startDate: "تاريخ البداية",
      start: "وقت البدء",
    venue: "المكان",
    yes: "نعم",
  },
};

const emptyEventForm = () => ({
  name: "",
  description: "",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date().toISOString().slice(0, 10),
  start_time: "09:00",
  end_time: "12:00",
  venue: "",
  allow_rsvp: true,
  attachments: [],
});

const formatEventDateRange = (startDate, endDate, language) => {
  const from = startDate || endDate;
  const to = endDate || startDate;
  if (!from) {
    return "";
  }
  if (from === to) {
    return formatDate(from, language);
  }
  return `${formatDate(from, language)} - ${formatDate(to, language)}`;
};

const DISABLED_SERVICE_TABS = new Set(["transport", "health", "meals", "hostel"]);
const MANAGEMENT_ROLES = ["platform_admin", "tenant_owner", "administrator", "principal"];

export default function Services() {
  const { user } = useAuth();
  const { t } = useI18n();
  const hideDormantServiceTabs = !!(user?.roles?.includes("administrator") || user?.roles?.includes("principal"));
  const showDormantServiceTabs = !hideDormantServiceTabs;
  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="services-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
          {t("management.servicesTitle")}
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t("management.servicesSubtitle")}
        </p>
      </div>
      <Tabs defaultValue="events">
        <TabsList className="flex-wrap h-auto">
          {showDormantServiceTabs && (
            <TabsTrigger
              value="transport"
              data-testid="tab-transport"
              disabled={DISABLED_SERVICE_TABS.has("transport")}
            >
              <Bus className="h-3.5 w-3.5 mr-1.5" /> Transport
            </TabsTrigger>
          )}
          {showDormantServiceTabs && (
            <TabsTrigger
              value="health"
              data-testid="tab-health"
              disabled={DISABLED_SERVICE_TABS.has("health")}
            >
              <Heart className="h-3.5 w-3.5 mr-1.5" /> Health
            </TabsTrigger>
          )}
          {showDormantServiceTabs && (
            <TabsTrigger
              value="meals"
              data-testid="tab-meals"
              disabled={DISABLED_SERVICE_TABS.has("meals")}
            >
              <Utensils className="h-3.5 w-3.5 mr-1.5" /> Meals
            </TabsTrigger>
          )}
          <TabsTrigger value="events" data-testid="tab-events">
            <PartyPopper className="h-3.5 w-3.5 mr-1.5" /> Events
          </TabsTrigger>
          {showDormantServiceTabs && (
            <TabsTrigger
              value="hostel"
              data-testid="tab-hostel"
              disabled={DISABLED_SERVICE_TABS.has("hostel")}
            >
              <Home className="h-3.5 w-3.5 mr-1.5" /> Hostel
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="transport">
          <TransportTab />
        </TabsContent>
        <TabsContent value="health">
          <HealthTab />
        </TabsContent>
        <TabsContent value="meals">
          <MealsTab />
        </TabsContent>
        <TabsContent value="events">
          <EventsTab />
        </TabsContent>
        <TabsContent value="hostel">
          <HostelTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------- Transport ----------------
function TransportTab() {
  const [busOpen, setBusOpen] = useState(false);
  const [routeOpen, setRouteOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const qc = useQueryClient();
  const { data: buses, isLoading } = useQuery({
    queryKey: ["buses"],
    queryFn: async () => unwrap(await api.get("/buses")),
  });
  const { data: routes } = useQuery({
    queryKey: ["routes"],
    queryFn: async () => unwrap(await api.get("/routes")),
  });
  const { data: students } = useQuery({
    queryKey: ["students-list"],
    queryFn: async () => unwrap(await api.get("/students")),
    enabled: assignOpen,
  });

  const [busForm, setBusForm] = useState({
    registration_no: "",
    capacity: 40,
    driver_name: "",
    driver_phone: "",
  });
  const [routeForm, setRouteForm] = useState({
    name: "",
    bus_id: "",
    stops: [],
  });
  const [assignForm, setAssignForm] = useState({
    student_id: "",
    pickup_stop_index: 0,
    drop_stop_index: 0,
  });

  const busMut = useMutation({
    mutationFn: async () => unwrap(await api.post("/buses", busForm)),
    onSuccess: () => {
      toast.success("Bus added");
      qc.invalidateQueries({ queryKey: ["buses"] });
      setBusOpen(false);
      setBusForm({
        registration_no: "",
        capacity: 40,
        driver_name: "",
        driver_phone: "",
      });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const routeMut = useMutation({
    mutationFn: async () => unwrap(await api.post("/routes", routeForm)),
    onSuccess: () => {
      toast.success("Route created");
      qc.invalidateQueries({ queryKey: ["routes"] });
      setRouteOpen(false);
      setRouteForm({ name: "", bus_id: "", stops: [] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const assignMut = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.post("/bus-assignments", {
          route_id: selectedRoute.id,
          ...assignForm,
        }),
      ),
    onSuccess: () => {
      toast.success("Student assigned");
      qc.invalidateQueries({ queryKey: ["routes"] });
      setAssignOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const addStop = () =>
    setRouteForm({
      ...routeForm,
      stops: [
        ...routeForm.stops,
        { name: "", lat: 0, lng: 0, pickup_time: "07:30", drop_time: "16:30" },
      ],
    });
  const updateStop = (i, field, val) => {
    const stops = [...routeForm.stops];
    stops[i] = { ...stops[i], [field]: val };
    setRouteForm({ ...routeForm, stops });
  };

  if (isLoading) return <Skeleton className="h-64 mt-6" />;

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-end gap-2">
        <Button
          onClick={() => setBusOpen(true)}
          className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
          data-testid="add-bus-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add bus
        </Button>
        <Button
          onClick={() => setRouteOpen(true)}
          className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
          data-testid="add-route-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add route
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-[var(--subtle)]">
          <CardHeader>
            <CardTitle className="text-sm font-display flex items-center gap-2">
              <Bus className="h-4 w-4 text-[var(--accent)]" /> Fleet
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm grid-table">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2">Reg No.</th>
                  <th className="text-left px-3 py-2">Driver</th>
                  <th className="text-right px-3 py-2">Capacity</th>
                  <th className="text-left px-3 py-2">GPS</th>
                </tr>
              </thead>
              <tbody>
                {(buses || []).map((b) => (
                  <tr key={b.id} data-testid={`bus-${b.id}`}>
                    <td className="px-3 py-2.5 font-mono-num text-xs">
                      {b.registration_no}
                    </td>
                    <td className="px-3 py-2.5">
                      {b.driver_name}
                      <div className="text-[10px] text-[var(--text-muted)] font-mono-num">
                        {b.driver_phone}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono-num">
                      {b.capacity}
                    </td>
                    <td className="px-3 py-2.5">
                      {b.last_position ? (
                        <Badge className="bg-[var(--success-bg)] text-[var(--success)] border-0">
                          <MapPin className="h-3 w-3 mr-0.5" />{" "}
                          {b.last_position.speed_kmh?.toFixed(0)} km/h
                        </Badge>
                      ) : (
                        <span className="text-xs text-[var(--text-muted)]">
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="border-[var(--subtle)]">
          <CardHeader>
            <CardTitle className="text-sm font-display flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[var(--accent)]" /> Routes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm grid-table">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-right px-3 py-2">Stops</th>
                  <th className="text-right px-3 py-2">Students</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {(routes || []).map((r) => (
                  <tr key={r.id} data-testid={`route-${r.id}`}>
                    <td className="px-3 py-2.5 font-medium">
                      {r.name}
                      <div className="text-[10px] text-[var(--text-muted)]">
                        {r.bus?.registration_no}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono-num">
                      {(r.stops || []).length}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono-num">
                      {r.assignment_count || 0}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedRoute(r);
                          setAssignForm({
                            student_id: "",
                            pickup_stop_index: 0,
                            drop_stop_index: 0,
                          });
                          setAssignOpen(true);
                        }}
                        data-testid={`assign-${r.id}`}
                      >
                        <UsersIcon className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Add Bus Dialog */}
      <Dialog open={busOpen} onOpenChange={setBusOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Add bus</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-semibold">
                Registration No.
              </Label>
              <Input
                value={busForm.registration_no}
                onChange={(e) =>
                  setBusForm({ ...busForm, registration_no: e.target.value })
                }
                className="mt-1"
                data-testid="bus-reg"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Capacity
              </Label>
              <Input
                type="number"
                value={busForm.capacity}
                onChange={(e) =>
                  setBusForm({
                    ...busForm,
                    capacity: parseInt(e.target.value) || 0,
                  })
                }
                className="mt-1"
                data-testid="bus-capacity"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Driver Name
              </Label>
              <Input
                value={busForm.driver_name}
                onChange={(e) =>
                  setBusForm({ ...busForm, driver_name: e.target.value })
                }
                className="mt-1"
                data-testid="bus-driver"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Driver Phone
              </Label>
              <Input
                value={busForm.driver_phone}
                onChange={(e) =>
                  setBusForm({ ...busForm, driver_phone: e.target.value })
                }
                className="mt-1"
                data-testid="bus-phone"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => busMut.mutate()}
              disabled={!busForm.registration_no || busMut.isPending}
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
              data-testid="save-bus"
            >
              {busMut.isPending ? "Saving..." : "Save bus"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Route Dialog */}
      <Dialog open={routeOpen} onOpenChange={setRouteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Add route</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-semibold">
                Route Name
              </Label>
              <Input
                value={routeForm.name}
                onChange={(e) =>
                  setRouteForm({ ...routeForm, name: e.target.value })
                }
                className="mt-1"
                data-testid="route-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">Bus</Label>
              <Select
                value={routeForm.bus_id}
                onValueChange={(v) => setRouteForm({ ...routeForm, bus_id: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select bus" />
                </SelectTrigger>
                <SelectContent>
                  {(buses || []).map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.registration_no}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase font-semibold">Stops</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={addStop}
                  data-testid="add-stop"
                >
                  <Plus className="h-3.5 w-3.5" /> Add stop
                </Button>
              </div>
              {routeForm.stops.map((s, i) => (
                <div key={i} className="grid grid-cols-2 gap-2 mb-2">
                  <Input
                    placeholder="Stop name"
                    value={s.name}
                    onChange={(e) => updateStop(i, "name", e.target.value)}
                    data-testid={`stop-name-${i}`}
                  />
                  <div className="flex gap-1">
                    <Input
                      type="time"
                      value={s.pickup_time}
                      onChange={(e) =>
                        updateStop(i, "pickup_time", e.target.value)
                      }
                      className="text-xs"
                    />
                    <Input
                      type="time"
                      value={s.drop_time}
                      onChange={(e) =>
                        updateStop(i, "drop_time", e.target.value)
                      }
                      className="text-xs"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => routeMut.mutate()}
              disabled={
                !routeForm.name || !routeForm.bus_id || routeMut.isPending
              }
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
              data-testid="save-route"
            >
              {routeMut.isPending ? "Saving..." : "Save route"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Student Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              Assign student — {selectedRoute?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-semibold">Student</Label>
              <Select
                value={assignForm.student_id}
                onValueChange={(v) =>
                  setAssignForm({ ...assignForm, student_id: v })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select student" />
                </SelectTrigger>
                <SelectContent>
                  {(students || []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.first_name} {s.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Pickup Stop
              </Label>
              <Select
                value={String(assignForm.pickup_stop_index)}
                onValueChange={(v) =>
                  setAssignForm({
                    ...assignForm,
                    pickup_stop_index: parseInt(v),
                  })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(selectedRoute?.stops || []).map((s, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {s.name} ({s.pickup_time})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Drop-off Stop
              </Label>
              <Select
                value={String(assignForm.drop_stop_index)}
                onValueChange={(v) =>
                  setAssignForm({ ...assignForm, drop_stop_index: parseInt(v) })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(selectedRoute?.stops || []).map((s, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {s.name} ({s.drop_time})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => assignMut.mutate()}
              disabled={!assignForm.student_id || assignMut.isPending}
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
              data-testid="save-assign"
            >
              {assignMut.isPending ? "Assigning..." : "Assign student"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Health ----------------
function HealthTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["clinic-visits"],
    queryFn: async () => unwrap(await api.get("/clinic-visits")),
  });
  if (isLoading) return <Skeleton className="h-64 mt-6" />;
  return (
    <div className="mt-6">
      <Card className="border-[var(--subtle)]">
        <CardHeader>
          <CardTitle className="text-sm font-display flex items-center gap-2">
            <Heart className="h-4 w-4 text-[var(--accent)]" /> Recent clinic
            visits
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm grid-table">
            <thead>
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Student</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-left px-3 py-2">Treatment</th>
                <th className="text-left px-3 py-2">Follow-up</th>
              </tr>
            </thead>
            <tbody>
              {(data || []).map((v) => (
                <tr key={v.id} data-testid={`visit-${v.id}`}>
                  <td className="px-3 py-2.5 font-mono-num text-xs">
                    {formatDate(v.visit_date)}
                  </td>
                  <td className="px-3 py-2.5 font-medium">
                    {v.student
                      ? `${v.student.first_name} ${v.student.last_name}`
                      : "—"}
                  </td>
                  <td className="px-3 py-2.5">{v.reason}</td>
                  <td className="px-3 py-2.5 text-[var(--text-secondary)]">
                    {v.treatment || "—"}
                  </td>
                  <td className="px-3 py-2.5">
                    {v.followup_required ? (
                      <Badge className="bg-[var(--warning-bg)] text-[var(--warning)] border-0">
                        Required
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
              {(data || []).length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="py-6 text-center text-[var(--text-muted)]"
                  >
                    No clinic visits recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------- Meals ----------------
function MealsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["meal-menus"],
    queryFn: async () => unwrap(await api.get("/meal-menus")),
  });
  const currentMenu = (data || [])[0];
  if (isLoading) return <Skeleton className="h-64 mt-6" />;

  return (
    <div className="mt-6">
      {!currentMenu ? (
        <Card className="border-[var(--subtle)]">
          <CardContent className="py-10 text-center text-[var(--text-muted)]">
            <Utensils className="h-8 w-8 mx-auto mb-2 opacity-40" /> No menu
            published yet.
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[var(--subtle)]">
          <CardHeader>
            <CardTitle className="text-sm font-display flex items-center gap-2">
              <Utensils className="h-4 w-4 text-[var(--accent)]" /> Week of{" "}
              {formatDate(currentMenu.week_start)}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm grid-table">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2">Day</th>
                  <th className="text-left px-3 py-2">Breakfast</th>
                  <th className="text-left px-3 py-2">Lunch</th>
                  <th className="text-left px-3 py-2">Snacks</th>
                </tr>
              </thead>
              <tbody>
                {(currentMenu.days || []).map((d) => (
                  <tr key={d.day_of_week}>
                    <td className="px-3 py-3 font-semibold">
                      {DAYS[d.day_of_week]}
                    </td>
                    <td className="px-3 py-3">{d.breakfast || "—"}</td>
                    <td className="px-3 py-3">{d.lunch || "—"}</td>
                    <td className="px-3 py-3">{d.snacks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------- Events ----------------
export function EventsTab({ embedded = false }) {
  const { user } = useAuth();
  const { language } = useI18n();
  const copyBase = EVENT_COPY[language] || EVENT_COPY.en;
  const mode = levelSystemOf(user);
  const copy = {
    ...copyBase,
    grade: levelWord(mode, language),
    chooseGrade: mode === "age_band" ? (language === "ar" ? "اختر الفئة العمرية" : "Choose age group") : copyBase.chooseGrade,
  };
  const canManageEvents = MANAGEMENT_ROLES.some((role) => user?.roles?.includes(role));
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [form, setForm] = useState(emptyEventForm);
  const [editForm, setEditForm] = useState(emptyEventForm);
  const [gradeId, setGradeId] = useState("");
  const [editGradeId, setEditGradeId] = useState("");
  const [validationError, setValidationError] = useState("");
  const [editValidationError, setEditValidationError] = useState("");
  const { data: events = [], isLoading, isError, refetch } = useQuery({ queryKey: ["events-admin"], queryFn: async () => unwrap(await api.get("/events")) });
  const { data: sections = [] } = useQuery({ queryKey: ["event-sections"], queryFn: async () => unwrap(await api.get("/sections")) });
  const { data: grades = [] } = useQuery({ queryKey: ["event-grades"], queryFn: async () => unwrap(await api.get("/grades")) });
  const sectionIdsByGrade = sections.reduce((acc, section) => {
    const current = acc;
    if (!section.grade_id) {
      return current;
    }
    current[section.grade_id] = current[section.grade_id] || [];
    current[section.grade_id].push(section.id);
    return current;
  }, {});
  const knownGradeIds = new Set();
  const gradeOptions = [];
  grades.forEach((grade) => {
    if (!sectionIdsByGrade[grade.id]?.length) {
      return;
    }
    knownGradeIds.add(grade.id);
    gradeOptions.push({ id: grade.id, name: levelDisplayName(grade, language, grades) || grade.id });
  });
  Object.keys(sectionIdsByGrade).forEach((fallbackGradeId) => {
    if (knownGradeIds.has(fallbackGradeId)) {
      return;
    }
    gradeOptions.push({ id: fallbackGradeId, name: fallbackGradeId });
  });
  const mut = useMutation({
    mutationFn: async (payload) => unwrap(await api.post("/events", payload)),
    onSuccess: () => { toast.success(copy.eventPublished); refetch(); setOpen(false); setForm(emptyEventForm()); setGradeId(""); setValidationError(""); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const updateMut = useMutation({
    mutationFn: async (payload) => unwrap(await api.patch(`/events/${editingEventId}`, payload)),
    onSuccess: () => {
      toast.success(copy.eventUpdated);
      refetch();
      setEditOpen(false);
      setEditingEventId(null);
      setEditForm(emptyEventForm());
      setEditGradeId("");
      setEditValidationError("");
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const validateEvent = (eventForm, setError) => {
    const name = (eventForm.name || "").trim();
    if (!name) {
      setError(copy.nameRequired);
      return null;
    }
    if (!eventForm.start_date || !eventForm.end_date) {
      setError(copy.dateRequired);
      return null;
    }
    if (eventForm.end_date < eventForm.start_date) {
      setError(copy.invalidDateRange);
      return null;
    }
    if (
      eventForm.start_date === eventForm.end_date
      && eventForm.start_time
      && eventForm.end_time
      && eventForm.start_time >= eventForm.end_time
    ) {
      setError(copy.invalidTime);
      return null;
    }
    setError("");
    return name;
  };

  const publishEvent = () => {
    const name = validateEvent(form, setValidationError);
    if (!name) {
      return;
    }
    const { attachments, ...eventForm } = form;
    mut.mutate({
      ...eventForm,
      name,
      attachment_file_ids: attachments.map((attachment) => attachment.id),
      audience_json: {
        roles: ["guardian"],
        grade_ids: gradeId ? [gradeId] : [],
        section_ids: gradeId ? (sectionIdsByGrade[gradeId] || []) : [],
      },
    });
  };

  const openEditDialog = (event) => {
    const audience = event.audience_json || {};
    const selectedSectionId = audience.section_ids?.[0] || "";
    const selectedGradeId =
      audience.grade_ids?.[0]
      || sections.find((section) => section.id === selectedSectionId)?.grade_id
      || "";
    setEditingEventId(event.id);
    setEditForm({
      name: event.name || "",
      description: event.description || "",
      start_date: event.start_date || event.event_date || new Date().toISOString().slice(0, 10),
      end_date: event.end_date || event.start_date || event.event_date || new Date().toISOString().slice(0, 10),
      start_time: event.start_time || "09:00",
      end_time: event.end_time || "12:00",
      venue: event.venue || "",
      allow_rsvp: event.allow_rsvp !== false,
      attachments: attachmentItemsFromRecord(event),
    });
    setEditGradeId(selectedGradeId);
    setEditValidationError("");
    setEditOpen(true);
  };

  const saveEventChanges = () => {
    const name = validateEvent(editForm, setEditValidationError);
    if (!name) {
      return;
    }
    const { attachments, ...eventForm } = editForm;
    updateMut.mutate({
      ...eventForm,
      name,
      attachment_file_ids: attachments.map((attachment) => attachment.id),
      audience_json: {
        roles: ["guardian"],
        grade_ids: editGradeId ? [editGradeId] : [],
        section_ids: editGradeId ? (sectionIdsByGrade[editGradeId] || []) : [],
      },
    });
  };

  if (isLoading) return <Skeleton className="h-64 mt-6" />;
  if (isError)
    return (
      <Card className="border-[var(--subtle)] mt-6">
        <CardContent className="py-10 text-center text-[var(--text-muted)]">
          <PartyPopper className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p>{copy.loadError}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            className="mt-3"
          >
            {copy.retry}
          </Button>
        </CardContent>
      </Card>
    );
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className={embedded ? "space-y-4" : "mt-6 space-y-4"}>
      <div className="flex items-center justify-end">
        <Dialog
          open={open}
          onOpenChange={(nextOpen) => {
            setOpen(nextOpen);
            if (!nextOpen) setValidationError("");
          }}
        >
          <DialogTrigger asChild>
            <Button
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
              data-testid="new-event-btn"
              disabled={!canManageEvents}
            >
              <Plus className="h-4 w-4 mr-1.5" /> {copy.newEvent}
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby={undefined} className="!fixed !inset-0 !z-50 !flex !h-auto !w-full !max-w-none !translate-x-0 !translate-y-0 !flex-col !gap-0 !overflow-hidden !border-0 !p-0 !shadow-none !rounded-none sm:!rounded-none">
            <DialogHeader className="shrink-0 border-b border-[var(--subtle)] bg-background px-4 py-4 pr-12 sm:px-6 sm:pr-12"><DialogTitle className="font-display">{copy.newEvent}</DialogTitle></DialogHeader>
            <div className="mx-auto min-h-0 w-full max-w-2xl flex-1 space-y-3 overflow-x-hidden overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
              <div><Label className="text-xs uppercase">{copy.name}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="event-name" /></div>
              <div><Label className="text-xs uppercase">{copy.description}</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 min-h-24 text-sm" /></div>
              <AnnouncementAttachmentPicker value={form.attachments} onChange={(attachments) => setForm({ ...form, attachments })} disabled={mut.isPending} inputId="event-attachments" />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="min-w-0"><Label htmlFor="event-start-date" className="text-xs uppercase">{copy.startDate}</Label><Input id="event-start-date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 min-w-0 w-full" data-testid="event-start-date" /></div>
                <div className="min-w-0"><Label htmlFor="event-end-date" className="text-xs uppercase">{copy.endDate}</Label><Input id="event-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 min-w-0 w-full" data-testid="event-end-date" /></div>
                <div className="min-w-0"><Label htmlFor="event-start-time" className="text-xs uppercase">{copy.start}</Label><Input id="event-start-time" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1 min-w-0 w-full" data-testid="event-start" /></div>
                <div className="min-w-0"><Label htmlFor="event-end-time" className="text-xs uppercase">{copy.end}</Label><Input id="event-end-time" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1 min-w-0 w-full" data-testid="event-end" /></div>
              </div>
              <div>
                <Label className="text-xs uppercase">{copy.audience}</Label>
                <Select value={gradeId || "all"} onValueChange={(value) => setGradeId(value === "all" ? "" : value)}>
                  <SelectTrigger className="mt-1" data-testid="event-grade-scope"><SelectValue placeholder={copy.chooseGrade} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{copy.allGuardians}</SelectItem>
                    {gradeOptions.map((grade) => <SelectItem key={grade.id} value={grade.id}>{grade.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {gradeId && <p className="text-xs text-[var(--text-muted)] mt-1">{copy.grade}</p>}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.allow_rsvp}
                  onCheckedChange={(checked) =>
                    setForm({ ...form, allow_rsvp: !!checked })
                  }
                  data-testid="event-rsvp-toggle"
                />
                <span>{copy.allowRsvp}</span>
              </div>
              {validationError && (
                <p className="text-sm text-[var(--error)]" role="alert">
                  {validationError}
                </p>
              )}
            </div>
            <DialogFooter className="shrink-0 border-t border-[var(--subtle)] bg-background px-4 py-4 sm:px-6">
              <Button onClick={publishEvent} disabled={mut.isPending} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="submit-event">{mut.isPending ? copy.publishing : copy.publish}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={editOpen} onOpenChange={(nextOpen) => { setEditOpen(nextOpen); if (!nextOpen) setEditValidationError(""); }}>
        <DialogContent aria-describedby={undefined} className="!fixed !inset-0 !z-50 !flex !h-auto !w-full !max-w-none !translate-x-0 !translate-y-0 !flex-col !gap-0 !overflow-hidden !border-0 !p-0 !shadow-none !rounded-none sm:!rounded-none">
          <DialogHeader className="shrink-0 border-b border-[var(--subtle)] bg-background px-4 py-4 pr-12 sm:px-6 sm:pr-12"><DialogTitle className="font-display">{copy.editEvent}</DialogTitle></DialogHeader>
          <div className="mx-auto min-h-0 w-full max-w-2xl flex-1 space-y-3 overflow-x-hidden overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
            <div><Label className="text-xs uppercase">{copy.name}</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1" data-testid="event-edit-name" /></div>
            <div><Label className="text-xs uppercase">{copy.description}</Label><Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="mt-1 min-h-24 text-sm" /></div>
            <AnnouncementAttachmentPicker value={editForm.attachments} onChange={(attachments) => setEditForm({ ...editForm, attachments })} disabled={updateMut.isPending} inputId="event-edit-attachments" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="min-w-0"><Label htmlFor="event-edit-start-date" className="text-xs uppercase">{copy.startDate}</Label><Input id="event-edit-start-date" type="date" value={editForm.start_date} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} className="mt-1 min-w-0 w-full" data-testid="event-edit-start-date" /></div>
              <div className="min-w-0"><Label htmlFor="event-edit-end-date" className="text-xs uppercase">{copy.endDate}</Label><Input id="event-edit-end-date" type="date" value={editForm.end_date} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })} className="mt-1 min-w-0 w-full" data-testid="event-edit-end-date" /></div>
              <div className="min-w-0"><Label htmlFor="event-edit-start-time" className="text-xs uppercase">{copy.start}</Label><Input id="event-edit-start-time" type="time" value={editForm.start_time} onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })} className="mt-1 min-w-0 w-full" data-testid="event-edit-start" /></div>
              <div className="min-w-0"><Label htmlFor="event-edit-end-time" className="text-xs uppercase">{copy.end}</Label><Input id="event-edit-end-time" type="time" value={editForm.end_time} onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })} className="mt-1 min-w-0 w-full" data-testid="event-edit-end" /></div>
            </div>
            <div><Label className="text-xs uppercase">{copy.venue}</Label><Input value={editForm.venue} onChange={(e) => setEditForm({ ...editForm, venue: e.target.value })} className="mt-1" data-testid="event-edit-venue" /></div>
            <div>
              <Label className="text-xs uppercase">{copy.audience}</Label>
              <Select value={editGradeId || "all"} onValueChange={(value) => setEditGradeId(value === "all" ? "" : value)}>
                <SelectTrigger className="mt-1" data-testid="event-edit-grade-scope"><SelectValue placeholder={copy.chooseGrade} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{copy.allGuardians}</SelectItem>
                  {gradeOptions.map((grade) => <SelectItem key={grade.id} value={grade.id}>{grade.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {editGradeId && <p className="text-xs text-[var(--text-muted)] mt-1">{copy.grade}</p>}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Checkbox checked={editForm.allow_rsvp} onCheckedChange={(checked) => setEditForm({ ...editForm, allow_rsvp: !!checked })} data-testid="event-edit-rsvp-toggle" />
              <span>{copy.allowRsvp}</span>
            </div>
            {editValidationError && <p className="text-sm text-[var(--error)]" role="alert">{editValidationError}</p>}
          </div>
          <DialogFooter className="shrink-0 border-t border-[var(--subtle)] bg-background px-4 py-4 sm:px-6">
            <Button onClick={saveEventChanges} disabled={updateMut.isPending} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="save-event-edit">
              {updateMut.isPending ? copy.publishing : copy.saveChanges}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid md:grid-cols-2 gap-3">
        {events.map((e) => {
          const eventStartDate = e.start_date || e.event_date;
          const eventEndDate = e.end_date || eventStartDate;
          const past = !!eventEndDate && eventEndDate < today;
          return (
            <Card
              key={e.id}
              className={`min-w-0 border-[var(--subtle)] border-t-4 bg-[var(--success-bg)] lift-card ${past ? "opacity-60" : ""}`}
              style={{ borderTopColor: "var(--success)" }}
              data-testid={`event-${e.id}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex flex-1 min-w-0 items-start gap-3">
                    <div className="h-10 w-10 rounded-md bg-[var(--success-bg)] text-[var(--success)] flex items-center justify-center flex-shrink-0">
                      <PartyPopper className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">{formatEventDateRange(eventStartDate, eventEndDate, language)}</div>
                      <div className="font-semibold text-[var(--text-primary)] mt-1 break-words">{e.name}</div>
                      <div className="text-xs text-[var(--text-muted)] mt-1">{e.venue}</div>
                      <div className="text-xs mt-2 flex items-center gap-3">
                        <span className="inline-flex items-center gap-1 text-[var(--success)]"><Check className="h-3 w-3" /> {e.rsvp_count || 0} {copy.yes}</span>
                        <Badge className={e.allow_rsvp ? "bg-[var(--info-bg)] text-[var(--info)] border-0" : "bg-[var(--subtle)] text-[var(--text-secondary)] border-0"}>{e.allow_rsvp ? copy.rsvpOpen : copy.rsvpClosed}</Badge>
                      </div>
                      <AnnouncementAttachmentList record={e} />
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
                    {canManageEvents && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(e)}
                        data-testid={`edit-event-${e.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1.5" /> {copy.editEvent}
                      </Button>
                    )}
                    {past && <Badge className="bg-[var(--subtle)] text-[var(--text-secondary)] border-0">Past</Badge>}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {events.length === 0 && (
        <Card className="border-[var(--subtle)]">
          <CardContent className="py-10 text-center text-[var(--text-muted)]">
            <PartyPopper className="h-8 w-8 mx-auto mb-2 opacity-40" />{" "}
            {copy.noEvents}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------- Hostel ----------------
function HostelTab() {
  const { data: hostels, isLoading } = useQuery({
    queryKey: ["hostels"],
    queryFn: async () => unwrap(await api.get("/hostels")),
  });
  const { data: rooms } = useQuery({
    queryKey: ["hostel-rooms"],
    queryFn: async () => unwrap(await api.get("/hostel-rooms")),
  });
  const { data: outpasses, refetch } = useQuery({
    queryKey: ["outpasses-admin"],
    queryFn: async () => unwrap(await api.get("/outpasses")),
  });
  const qc = useQueryClient();
  const decideMut = useMutation({
    mutationFn: async ({ id, decision }) =>
      unwrap(await api.post(`/outpasses/${id}/decision`, { decision })),
    onSuccess: () => {
      toast.success("Decision recorded");
      refetch();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (isLoading) return <Skeleton className="h-64 mt-6" />;

  return (
    <div className="mt-6 grid md:grid-cols-2 gap-4">
      <Card className="border-[var(--subtle)]">
        <CardHeader>
          <CardTitle className="text-sm font-display flex items-center gap-2">
            <Home className="h-4 w-4 text-[var(--accent)]" /> Hostels
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {(hostels || []).map((h) => (
            <div
              key={h.id}
              className="border border-[var(--subtle)] rounded p-3 flex items-center justify-between"
              data-testid={`hostel-${h.id}`}
            >
              <div>
                <div className="font-semibold">{h.name}</div>
                <div className="text-xs text-[var(--text-muted)] capitalize">
                  {h.gender}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono-num font-bold">
                  {h.occupied_count}/{h.room_count * 3}
                </div>
                <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-widest">
                  occupied
                </div>
              </div>
            </div>
          ))}
          <div className="mt-4">
            <div className="text-xs font-semibold uppercase text-[var(--text-secondary)] tracking-widest mb-2">
              Rooms
            </div>
            <div className="grid grid-cols-5 gap-2">
              {(rooms || []).map((r) => {
                const full = r.occupied >= r.capacity;
                return (
                  <div
                    key={r.id}
                    className={`rounded border p-2 text-center ${full ? "bg-[var(--error-bg)] border-[var(--error)]" : "bg-[var(--success-bg)] border-[var(--success)]"}`}
                  >
                    <div className="font-mono-num font-bold text-sm">
                      {r.room_number}
                    </div>
                    <div className="text-[10px]">
                      {r.occupied}/{r.capacity}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[var(--subtle)]">
        <CardHeader>
          <CardTitle className="text-sm font-display flex items-center gap-2">
            <Clock className="h-4 w-4 text-[var(--accent)]" /> Outpass requests
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(outpasses || [])
            .filter((o) => o.status === "requested")
            .map((o) => (
              <div
                key={o.id}
                className="border border-[var(--subtle)] rounded p-3"
                data-testid={`outpass-${o.id}`}
              >
                <div className="font-semibold text-sm">
                  {o.student
                    ? `${o.student.first_name} ${o.student.last_name}`
                    : "—"}
                </div>
                <div className="text-xs text-[var(--text-muted)]">
                  {formatDateTime(o.from_datetime)} →{" "}
                  {formatDateTime(o.to_datetime)}
                </div>
                <div className="text-xs mt-1">{o.reason}</div>
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      decideMut.mutate({ id: o.id, decision: "approved" })
                    }
                    className="h-8 bg-[var(--success)] hover:bg-emerald-700 text-white"
                    data-testid={`approve-${o.id}`}
                  >
                    <Check className="h-3 w-3 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    onClick={() =>
                      decideMut.mutate({ id: o.id, decision: "rejected" })
                    }
                    variant="outline"
                    className="h-8"
                    data-testid={`reject-${o.id}`}
                  >
                    <X className="h-3 w-3 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          {(outpasses || []).filter((o) => o.status === "requested").length ===
            0 && (
            <div className="text-sm text-[var(--text-muted)]">
              No pending requests.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
